import React, { useCallback, useMemo } from 'react';
import {
  CaseSearchDocument,
  CaseSearchEdge,
  HistoryAction,
  HistoryType,
  SearchSortType,
} from '__generated__/graphql';
import { CaseSearchResult } from 'pages/cases/CaseSearchResult';
import { TypedSearchResults } from '../TypedSearchResults';
import { EmptyResults } from '../EmptyResults';
import styled from 'styled-components';
import TitledPage from 'components/common/TitledPage';
import {
  StyledFormCheckbox,
  StyledSearchCheckboxes,
  StyledSearchContent,
  StyledSearchHeader,
  StyledSearchResults,
} from '../SearchStyles';
import { SearchFilters, StyledSearchWithin } from '../SearchFilters';
import { FilterChips } from '../FilterChips';
import { QueryInput } from 'components/common/query-input';
import { CasesFilters } from './CasesFilters';
import useSearchVariables from '../useSearchVariables';
import { SearchWithin } from '../SearchWithin';
import { TrackHistory } from 'hooks/useHistory';

export function CasesSearch() {
  const mutateVariables: NonNullable<Parameters<typeof useSearchVariables>[0]> =
    useCallback(
      (variables, queryString) => ({
        ...variables,
        sort: (queryString.sort as SearchSortType) || undefined,
        published: queryString.published || undefined,
        caseDateGte:
          queryString.yearSearch === 'custom'
            ? queryString.yearGte
            : queryString.yearSearch,
        caseDateLte:
          queryString.yearSearch === 'custom'
            ? queryString.yearLte
            : parseInt(queryString.yearSearch) + 1,
      }),
      [],
    );

  const variables = useSearchVariables(mutateVariables);

  return (
    <TitledPage title="Search Cases">
      <TrackHistory
        text={variables.query}
        type={HistoryType.Case}
        action={HistoryAction.Search}
        subtype="SEARCH_CASE"
      />

      <SearchFilters queryVariables={['textFilters', 'optionFilters']}>
        <CasesFilters />
      </SearchFilters>
      <StyledSearchContent>
        <StyledSearchHeader>
          <div>
            <StyledSearchWithin>{<SearchWithin />}</StyledSearchWithin>

            <StyledSearchCheckboxes>
              <QueryInput
                through={StyledFormCheckbox}
                type="checkbox"
                name="sort"
                placeholder="Sort by date"
                value="DATE"
                className="date-sort"
                mutate={() => ({ page: undefined })}
              />
              <QueryInput
                through={StyledFormCheckbox}
                type="checkbox"
                name="published"
                placeholder="Show Published Only"
                value="PUBLISHED"
                mutate={() => ({ page: undefined })}
              />
            </StyledSearchCheckboxes>
          </div>
          <div>
            <FilterChips
              filters={{
                Courts: 'court_',
                'Cause of Action': 'coa_',
                'Procedural Posture': 'postures_',
                Disposition: 'dispositions_',
                "Appellant's Trial Court Role": 'appealingparties_',
                Year: {
                  hasChip: searchParams =>
                    searchParams.get('yearSearch') !== null,
                  onRemove: searchParams => {
                    searchParams.delete('yearSearch');
                    searchParams.delete('yearGte');
                    searchParams.delete('yearLte');
                  },
                },
              }}
            />
          </div>
        </StyledSearchHeader>

        <StyledCaseResults>
          <TypedSearchResults
            query={CaseSearchDocument}
            emptyResults={<EmptyResults />}
            variables={variables}
          >
            {result => (
              <StyledCasesSearchResult
                key={(result as CaseSearchEdge).node.judicataId}
                result={result as CaseSearchEdge}
              />
            )}
          </TypedSearchResults>
        </StyledCaseResults>
      </StyledSearchContent>
    </TitledPage>
  );
}

const StyledCaseResults = styled(StyledSearchResults)`
  padding-top: 30px;
`;

const StyledCasesSearchResult = styled(CaseSearchResult)`
  .result {
    margin-left: 0;
  }
`;

StyledCasesSearchResult.displayName = 'StyledCasesSearchResult';
